import { useNavigate } from 'react-router-dom'
import { Play, FolderOpen, ClipboardCheck, Network, CheckSquare, BarChart3, TrendingUp, Grid3x3 } from 'lucide-react'

export default function Hub() {
  const navigate = useNavigate()

  const modules = [
    {
      id: 'carpe-diem',
      name: 'Carpe Diem - Seize the Day',
      description: 'Mindfulness at Work - What are you working on right now?',
      icon: Play,
      color: 'bg-blue-500',
      hoverColor: 'hover:bg-blue-600',
      available: true,
      status: 'available' as const,
      path: '/carpe-diem',
    },
    {
      id: 'project-management',
      name: 'Project Management',
      description: 'Manage Projects, Tasks & Drive Integration',
      icon: FolderOpen,
      color: 'bg-green-500',
      hoverColor: 'hover:bg-green-600',
      available: true,
      status: 'prototype' as const,
      path: '/projects',
    },
    {
      id: 'inspection-reports',
      name: 'Inspection Reports',
      description: 'Create AI-powered inspection reports from video walkthroughs',
      icon: ClipboardCheck,
      color: 'bg-orange-500',
      hoverColor: 'hover:bg-orange-600',
      available: true,
      status: 'prototype' as const,
      path: '/inspections',
    },
    {
      id: 'team-brevity',
      name: 'Team Brevity',
      description: 'What you do here - Org chart, directory, locations & teams',
      icon: Network,
      color: 'bg-purple-500',
      hoverColor: 'hover:bg-purple-600',
      available: true,
      status: 'prototype' as const,
      path: '/team-brevity',
    },
    {
      id: 'pwf-tasks',
      name: 'ProWorkflow Tasks',
      description: 'Tasks due today, tomorrow & next week from ProWorkflow',
      icon: CheckSquare,
      color: 'bg-emerald-500',
      hoverColor: 'hover:bg-emerald-600',
      available: true,
      status: 'prototype' as const,
      path: '/pwf-tasks',
    },
    {
      id: 'dashboard',
      name: 'Analytics Dashboard',
      description: 'Business analytics – NPS, margins, client feedback',
      icon: BarChart3,
      color: 'bg-slate-600',
      hoverColor: 'hover:bg-slate-700',
      available: true,
      status: 'prototype' as const,
      path: '/dashboard',
    },
    {
      id: 'profitability-performance',
      name: 'Profitability & Performance',
      description: 'Track margins, delivery performance, and financial health across projects',
      icon: TrendingUp,
      color: 'bg-indigo-500',
      hoverColor: 'hover:bg-indigo-600',
      available: true,
      status: 'prototype' as const,
      path: '/profitability-performance',
    },
    {
      id: 'work-types-matrix',
      name: 'Work Types Matrix',
      description: 'Org task catalog by team — Pipedrive work types under Delivery Team',
      icon: Grid3x3,
      color: 'bg-teal-500',
      hoverColor: 'hover:bg-teal-600',
      available: true,
      status: 'prototype' as const,
      path: '/work-types-matrix',
    },
  ]

  const skeletonTiles: { id: string; available: boolean }[] = []

  const handleModuleClick = (module: (typeof modules)[0]) => {
    if (module.available) {
      navigate(module.path)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Welcome to Brevity AI</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Your central hub for automation, insights, and operational excellence. Choose a module
            below to get started.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {modules.map((module) => {
            const IconComponent = module.icon
            return (
              <div
                key={module.id}
                onClick={() => handleModuleClick(module)}
                className="relative group card transition-all duration-300 transform hover:scale-105 hover:shadow-lg cursor-pointer"
              >
                <div className="absolute top-4 right-4">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      module.status === 'prototype'
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-green-100 text-green-800'
                    }`}
                  >
                    {module.status === 'prototype' ? 'Prototype' : 'Available'}
                  </span>
                </div>

                <div
                  className={`w-16 h-16 rounded-xl flex items-center justify-center mb-4 transition-colors duration-300 ${module.color} ${module.hoverColor}`}
                >
                  <IconComponent className="w-8 h-8 text-white" />
                </div>

                <div className="space-y-2">
                  <h3 className="text-xl font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
                    {module.name}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{module.description}</p>
                </div>
              </div>
            )
          })}

          {skeletonTiles.map((tile) => (
            <div key={tile.id} className="card opacity-40">
              <div className="w-16 h-16 rounded-xl bg-gray-200 flex items-center justify-center mb-4">
                <div className="w-8 h-8 bg-gray-300 rounded" />
              </div>
              <div className="space-y-2">
                <div className="h-6 bg-gray-200 rounded w-3/4" />
                <div className="h-4 bg-gray-200 rounded w-full" />
                <div className="h-4 bg-gray-200 rounded w-2/3" />
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="card text-center">
            <div className="text-2xl font-bold text-green-600 mb-2">
              {modules.filter((m) => m.status === 'available').length}
            </div>
            <div className="text-sm text-gray-600">Available</div>
          </div>
          <div className="card text-center">
            <div className="text-2xl font-bold text-amber-600 mb-2">
              {modules.filter((m) => m.status === 'prototype').length}
            </div>
            <div className="text-sm text-gray-600">Prototype</div>
          </div>
          <div className="card text-center">
            <div className="text-2xl font-bold text-blue-600 mb-2">∞</div>
            <div className="text-sm text-gray-600">Possibilities</div>
          </div>
        </div>
      </main>
    </div>
  )
}
